import {
  flip,
  offset,
  shift,
  size,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";

import ChevronIcon from "@assets/icons/chevron-down.svg";

import { useCloseOnScroll } from "@hooks/useCloseOnScroll";
import { useEscapeKey } from "@hooks/useEscapeKey";
import { IAttribute, IRelationObject } from "@interfaces/attribute.interface";
import { ITagResponse } from "@interfaces/responses.interface";
import { FieldErrorMessage } from "@ui/FormElements";
import { cn } from "@utils/tailwind.util";

import { Tag as TagItem } from "@components/Tags/Tag";

import { RelationTagPicker } from "./RelationTagPicker";
import {
  haveSameRelations,
  isSingleSelectRelation,
  removeRelation,
  sortRelationsForDisplay,
  toggleRelation,
  toTagResponse,
} from "./utils";

/** Matches the other dropdowns in the forms. */
const MAX_PANEL_HEIGHT = 360;

/** Gap between the field and its panel. */
const PANEL_OFFSET = 4;

/** Breathing room kept from the viewport edges. */
const VIEWPORT_PADDING = 8;

interface RelationAttributeInputProps {
  attribute: IAttribute;
  /** Rendered as pills; the caller owns this state. */
  value: IRelationObject[];
  onChange: (next: IRelationObject[]) => void;
  /** The settled set, once per batch — this is what hits the API. */
  onSave?: (next: IRelationObject[]) => void;
  /** Never offered by the search — e.g. the record being edited. */
  excludedIds?: string[];
  disabled?: boolean;
  /** Paints the field red; pair with `errorMessage` so the border and the message agree. */
  isInvalid?: boolean;
  errorMessage?: string;
  /** Empty-state prompt, defaulting to `Select {attribute.title}`. */
  placeholder?: string;
  className?: string;
}

/** Data Objects field: removable pills plus the tagging dropdown. Holds no value. */
export const RelationAttributeInput = ({
  attribute,
  value,
  onChange,
  onSave,
  excludedIds,
  disabled = false,
  isInvalid = false,
  errorMessage,
  placeholder,
  className,
}: RelationAttributeInputProps) => {
  const [isOpen, setIsOpen] = useState(false);

  // Applied last in the `cn` below so it outranks the hover and open variants: tailwind-merge keeps
  // the later class. The dark: copies are needed because it dedupes per variant, not across them.
  const invalidClassName =
    isInvalid || errorMessage
      ? "border-red-600 hover:border-red-600 ring-red-600 dark:border-red-600 dark:hover:border-red-600"
      : undefined;

  const isSingleSelect = isSingleSelectRelation(attribute);

  // Show pills in the order the backend hands them back, so saving never reshuffles them.
  const orderedValue = useMemo(() => sortRelationsForDisplay(value), [value]);

  const selectedRecordIds = useMemo(
    () => orderedValue.map((relation) => relation.object.id),
    [orderedValue]
  );

  // Multi keeps tagged records listed (ticked) so they can be unticked; single hides its value.
  const searchExcludedIds = useMemo(
    () => (isSingleSelect ? [...(excludedIds ?? []), ...selectedRecordIds] : excludedIds),
    [excludedIds, isSingleSelect, selectedRecordIds]
  );

  // Synchronous mirror: single-select edits and closes in one tick, before `value` updates.
  const latestRef = useRef(value);
  const baselineRef = useRef(value);

  useEffect(() => {
    latestRef.current = value;
  }, [value]);

  const save = useCallback(
    (relations: IRelationObject[]) => {
      baselineRef.current = relations;
      onSave?.(relations);
    },
    [onSave]
  );

  const updateValue = (next: IRelationObject[]) => {
    latestRef.current = next;
    onChange(next);
  };

  const setOpen = useCallback(
    (next: boolean) => {
      if (next) {
        baselineRef.current = latestRef.current;
      } else if (!haveSameRelations(latestRef.current, baselineRef.current)) {
        // One write, however many boxes were ticked.
        save(latestRef.current);
      }

      setIsOpen(next);
    },
    [save]
  );

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setOpen,
    placement: "bottom-start",
    strategy: "fixed",
    // No `autoUpdate`: scrolling dismisses the panel, so there is nothing to follow.
    middleware: [
      offset(PANEL_OFFSET),
      flip({ padding: VIEWPORT_PADDING }),
      shift({ padding: VIEWPORT_PADDING }),
      // Field width, list inside the viewport.
      size({
        padding: VIEWPORT_PADDING,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${Math.min(availableHeight, MAX_PANEL_HEIGHT)}px`,
          });
        },
      }),
    ],
  });

  const close = useCallback(() => setOpen(false), [setOpen]);

  // Flush on unmount, or a form closed mid-edit would strand the batch.
  const saveRef = useRef(save);
  saveRef.current = save;
  useEffect(
    () => () => {
      if (!haveSameRelations(latestRef.current, baselineRef.current)) {
        saveRef.current(latestRef.current);
      }
    },
    []
  );

  const handleSelect = (relation: IRelationObject) => {
    const { relations } = toggleRelation(latestRef.current, relation, isSingleSelect);

    updateValue(relations);

    // Nothing left to accumulate; closing commits it.
    if (isSingleSelect) close();
  };

  const handleRemove = (tag: ITagResponse) => {
    const removed = orderedValue.find((relation) => relation.id === tag.id);
    if (!removed) return;

    const next = removeRelation(latestRef.current, removed);

    updateValue(next);
    // Standalone action — no batch to join.
    save(next);
  };

  // Outside-press via floating-ui (as in LocationAutocomplete); Escape stays on the stacked hook.
  const { getReferenceProps, getFloatingProps } = useInteractions([
    useDismiss(context, { escapeKey: false }),
  ]);

  useEscapeKey({ callback: close, enabled: isOpen });

  // Scrolling dismisses the panel; closing still commits the batch.
  useCloseOnScroll(isOpen, close, refs.domReference, refs.floating);

  return (
    <div className="relative flex w-full flex-col">
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-expanded={isOpen}
        aria-disabled={disabled}
        aria-label={placeholder ?? `Select ${attribute.title}`}
        className={cn(
          "flex min-h-[38px] w-full flex-wrap items-center gap-1 rounded-md border px-2 py-1.5",
          "border-stroke-primary bg-white text-sm dark:border-gray-600 dark:bg-gray-850",
          disabled
            ? "cursor-not-allowed opacity-60"
            : "cursor-pointer hover:border-stroke-secondary",
          isOpen && "border-stroke-actionPrimary ring-1 ring-stroke-actionPrimary",
          invalidClassName,
          className
        )}
        onClick={() => !disabled && setOpen(!isOpen)}
        onKeyDown={(event) => {
          if (disabled) return;
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setOpen(!isOpen);
          }
        }}
      >
        {orderedValue.length > 0 ? (
          orderedValue.map((relation) => (
            // Isolate pill clicks so they don't toggle the dropdown.
            <span
              key={relation.id}
              className="flex min-w-0 max-w-full"
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
            >
              <TagItem
                data={toTagResponse(relation)}
                onRemove={disabled ? undefined : handleRemove}
              />
            </span>
          ))
        ) : (
          // `min-w-0` so the flex item may shrink; without it `truncate` never kicks in and a long
          // attribute title wraps onto a second line, growing the field.
          <span className="min-w-0 flex-1 truncate px-1 text-sm font-normal text-content-disabled">
            {placeholder ?? `Select ${attribute.title}`}
          </span>
        )}

        <ChevronIcon
          width={16}
          height={16}
          className={cn(
            "ml-auto shrink-0 text-content-secondary transition-transform dark:text-white",
            isOpen && "rotate-180"
          )}
        />
      </div>

      {isOpen && (
        <div
          ref={refs.setFloating}
          {...getFloatingProps()}
          // Rendered inline, not portaled: the dialogs these forms live in trap focus and treat a
          // click in an outside portal as a dismiss, so a portaled panel is unreachable there.
          style={{ ...floatingStyles, zIndex: 50 }}
          className={cn(
            // No `min-h-*`: it would out-rank the max-height `size` computes.
            "flex min-w-0 flex-col",
            "items-center justify-center rounded-md border border-stroke-primary dark:border-gray-600",
            "divide-y divide-stroke-primary bg-white text-content-primary shadow-navItem",
            "dark:divide-gray-600 dark:bg-gray-850"
          )}
        >
          <RelationTagPicker
            attribute={attribute}
            onSelect={handleSelect}
            excludedIds={searchExcludedIds}
            selectedRecordIds={selectedRecordIds}
            // Checkboxes are the affordance for ticking and unticking a set; a single-select
            // field has nothing to untick, so it reads as a plain option list.
            showSelectedCheck={!isSingleSelect}
          />
        </div>
      )}

      <FieldErrorMessage message={errorMessage} />
    </div>
  );
};
